import React, { useState, useEffect, useCallback, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { useNavigate, useParams } from '@reach/router';
import { debounce } from 'debounce';

import { useAgilyticsState } from '@agilytics/global-state';
import { getProjects } from '@agilytics/utils/jira';
import { useProjects, useCloudIds } from '@agilytics/hooks';
import { GridContainer, Heading, Paragraph, Menu, MenuItem } from '@agilytics/components';

const Container = styled.section`
  max-width: 500px;
  margin: auto;
  padding: ${({ theme }) => theme.spacingBase};
  text-align: center;
`;

const FilterInput = styled.input`
  font-family: ${({ theme }) => theme.fonts.main};
  padding: ${({ theme }) => theme.spacingBase};
  border: 2px solid ${({ theme }) => theme.colors.outline};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacingTriple};
`;

const ChevronImage = styled.aside`
  width: 100%;
  text-align: right;
  & > svg {
    width: 40px;
    transform: scaleX(-1);
  }
`;

const ProjectName = styled(Paragraph)`
  font-weight: 400;
  margin: 0;
`;

const ProjectCode = styled(Paragraph)`
  margin-top: ${({ theme }) => theme.spacingBase};
  font-weight: 200;
  font-size: ${({ theme }) => theme.fontSizes.legal};
  color: ${({ theme }) => theme.colors.outlineBg};
`;

export default function ProjectsPicker() {
  const [filteredProjects, setFilteredProjects] = useState([]);
  const navigate = useNavigate();
  const { cloudId } = useParams();
  useCloudIds();

  const themeContext = useContext(ThemeContext);
  const { accessToken } = useAgilyticsState();

  const selectProject = useCallback(
    projectId => {
      navigate(`/projects/${cloudId}/${projectId}`);
    },
    [navigate, cloudId]
  );

  const initialProjects = useProjects();

  useEffect(() => {
    setFilteredProjects(initialProjects);
    if (initialProjects.length === 1) {
      selectProject(initialProjects[0].key);
    }
  }, [setFilteredProjects, initialProjects, selectProject]);

  const DEBOUNCED_SEARCH_MS = 500;
  const searchForProject = debounce(
    async e => {
      const query = e.target.value;
      const projects = await getProjects(accessToken, cloudId, query);
      setFilteredProjects(projects);
    },
    DEBOUNCED_SEARCH_MS,
    true
  );

  return (
    <GridContainer>
      <Container>
        <Heading level={3}>select your project</Heading>
        <FilterInput type="text" placeholder="search for your project" onChange={searchForProject} />
        <Menu>
          {filteredProjects.map((project, index) => {
            return (
              <MenuItem key={index} onClick={selectProject.bind(this, project.key)}>
                <img src={project.avatarUrls['48x48'] || project.avatarUrls['24x24']} alt={project.name} />
                <ProjectName>{project.name}</ProjectName>
                <ProjectCode>{project.key}</ProjectCode>
                <ChevronImage>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" version="1.1" x="0px" y="0px">
                    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g fillRule="nonzero" fill={themeContext.colors.brand}>
                        <path d="M10.7071068,3.70710678 C11.0976311,3.31658249 11.0976311,2.68341751 10.7071068,2.29289322 C10.3165825,1.90236893 9.68341751,1.90236893 9.29289322,2.29289322 L4.29289322,7.29289322 C3.90236893,7.68341751 3.90236893,8.31658249 4.29289322,8.70710678 L9.29289322,13.7071068 C9.68341751,14.0976311 10.3165825,14.0976311 10.7071068,13.7071068 C11.0976311,13.3165825 11.0976311,12.6834175 10.7071068,12.2928932 L6.41421356,8 L10.7071068,3.70710678 Z"></path>
                      </g>
                    </g>
                  </svg>
                </ChevronImage>
              </MenuItem>
            );
          })}
        </Menu>
      </Container>
    </GridContainer>
  );
}
